/**
 * HiringTracker — monthly hires vs separations + growth trend + seasonality.
 * All numbers derived from clinicians.start_date + termination_date +
 * hr_separations. Works today even before HR back-fills exit reasons.
 */
import React, { useEffect, useState } from 'react';
import api from '../../lib/api';
import { fmtMonYY } from '../../lib/date';
import { Card } from '../ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, LineChart,
  BarChart, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ReferenceLine,
} from 'recharts';

const C = {
  ink: '#2D2833', ink2: '#685B72', soft: '#8A7D95',
  border: '#E8E5EC', paper: '#FBF8F4',
  plum: '#4A3B5F', gold: '#B08A3A', ok: '#3B6F5D', rose: '#8A3A3A',
};

function Kpi({ label, value, sub, tone, testId }) {
  return (
    <Card className="p-4 border text-center" style={{ borderColor: C.border }} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: tone }}>{label}</div>
      <div className="mt-1 text-3xl font-serif" style={{ color: C.ink }}>{value}</div>
      {sub && <div className="mt-1 text-[11px]" style={{ color: C.soft }}>{sub}</div>}
    </Card>
  );
}

export default function HiringTracker() {
  const [months, setMonths] = useState(24);
  const [role, setRole] = useState('All');   // 'All' | 'SLP' | 'CF' | 'SLPA'
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get('/hr/hiring-tracker', { params: { months } })
      .then(({ data }) => setData(data))
      .catch(() => toast.error('Could not load hiring tracker'))
      .finally(() => setLoading(false));
  }, [months]);

  if (loading) return <div className="p-8 text-center text-sm" style={{ color: C.soft }}>Loading hiring tracker…</div>;
  if (!data) return null;

  const t = data.totals || {};
  const roles = data.roles || ['SLP', 'CF', 'SLPA'];
  const activeRole = role === 'All' ? null : role;

  // For All: use totals (hires/separations). For a specific role: use per-role fields.
  const pickHires = (r) => activeRole ? (r[`hires_${activeRole}`] || 0) : (r.hires || 0);
  const pickSeps  = (r) => activeRole ? (r[`seps_${activeRole}`]  || 0) : (r.separations || 0);

  // Pre-shape for the diverging bar (separations negative).
  const chart = (data.series || []).map(r => {
    const h = pickHires(r);
    const s = pickSeps(r);
    return { ...r, hires_view: h, separations_view: s, seps_neg_view: -Math.abs(s), net_view: h - s };
  });

  // KPI figures adapt to the role filter using totals_by_role when available.
  const totalsForRole = activeRole ? (data.totals_by_role?.[activeRole] || {}) : null;
  const kpiHires12    = totalsForRole ? totalsForRole.hires_last_12mo       : (t.hires_last_12mo ?? 0);
  const kpiSeps12     = totalsForRole ? totalsForRole.separations_last_12mo : (t.separations_last_12mo ?? 0);
  const kpiNet12      = kpiHires12 - kpiSeps12;
  const kpiHiresWin   = totalsForRole ? totalsForRole.hires_window       : (t.hires_window ?? 0);
  const kpiSepsWin    = totalsForRole ? totalsForRole.separations_window : (t.separations_window ?? 0);
  const kpiNetWin     = kpiHiresWin - kpiSepsWin;

  // Seasonality — All uses backend seasonality; per-role reshape from series.
  const seasonality = activeRole ? (() => {
    const buckets = Array.from({ length: 12 }, (_, i) => ({
      month_num: i + 1,
      month_label: ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][i],
      hires: 0, separations: 0,
    }));
    for (const r of (data.series || [])) {
      const m = parseInt(r.month.split('-')[1], 10);
      if (m >= 1 && m <= 12) {
        buckets[m - 1].hires += r[`hires_${activeRole}`] || 0;
        buckets[m - 1].separations += r[`seps_${activeRole}`] || 0;
      }
    }
    return buckets;
  })() : (data.seasonality || []);

  const roleLabel = activeRole || 'All roles';

  return (
    <div className="space-y-4" data-testid="hr-hiring-tracker">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: C.gold }}>
          Hiring & Separations Tracker
        </div>
        {/* Role slicer chips */}
        <div className="flex items-center gap-1 ml-2" data-testid="hr-tracker-role-chips">
          {['All', ...roles].map((r) => {
            const on = role === r;
            return (
              <button
                key={r}
                onClick={() => setRole(r)}
                data-testid={`hr-tracker-role-${r}`}
                className="text-[11px] px-2.5 py-1 rounded-full border transition-colors"
                style={{
                  borderColor: on ? C.plum : C.border,
                  background:  on ? C.plum : 'transparent',
                  color:       on ? '#fff' : C.ink2,
                  fontWeight:  on ? 600 : 400,
                }}>{r}</button>
            );
          })}
        </div>
        <div className="ml-auto">
          <Select value={String(months)} onValueChange={v => setMonths(parseInt(v, 10))}>
            <SelectTrigger className="w-40 h-8" data-testid="hr-tracker-window">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="12">Last 12 months</SelectItem>
              <SelectItem value="24">Last 24 months</SelectItem>
              <SelectItem value="36">Last 36 months</SelectItem>
              <SelectItem value="60">Last 5 years</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid md:grid-cols-4 gap-3">
        <Kpi testId="hr-tracker-hires-12" label={`Hires · last 12 mo · ${roleLabel}`}
             value={kpiHires12}
             sub={`${kpiHiresWin} in ${months}-mo window`} tone={C.ok} />
        <Kpi testId="hr-tracker-seps-12" label={`Separations · last 12 mo · ${roleLabel}`}
             value={kpiSeps12}
             sub={`${kpiSepsWin} in ${months}-mo window`} tone={C.rose} />
        <Kpi testId="hr-tracker-net-12" label={`Net growth · last 12 mo · ${roleLabel}`}
             value={kpiNet12 >= 0 ? `+${kpiNet12}` : kpiNet12}
             sub={`${kpiNetWin >= 0 ? '+' : ''}${kpiNetWin} in ${months}-mo window`}
             tone={kpiNet12 >= 0 ? C.ok : C.rose} />
        <Kpi testId="hr-tracker-current" label="Active today (all roles)"
             value={(data.series?.[data.series.length - 1]?.active_end) ?? '—'}
             sub={`vs ${data.series?.[0]?.active_end ?? '—'} at start of window`} tone={C.plum} />
      </div>

      {/* Hires vs Separations diverging bar chart */}
      <Card className="p-4 border" style={{ borderColor: C.border }} data-testid="hr-tracker-flow-chart">
        <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: C.gold }}>
          Hires vs Separations · monthly · {roleLabel}
        </div>
        <div style={{ width: '100%', height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
              <XAxis dataKey="month" fontSize={10} tickLine={false} tickFormatter={fmtMonYY} interval={Math.floor(chart.length / 12) || 0} />
              <YAxis fontSize={10} tickLine={false} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <ReferenceLine y={0} stroke="#000" strokeOpacity={0.2} />
              <Bar dataKey="hires_view" name="Hires" fill={C.ok} radius={[4,4,0,0]} maxBarSize={22} />
              <Bar dataKey="seps_neg_view" name="Separations" fill={C.rose} radius={[0,0,4,4]} maxBarSize={22} />
              <Line type="monotone" dataKey="net_view" name="Net" stroke={C.plum} strokeWidth={2} dot={{ r: 2 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Active-headcount growth curve */}
      <Card className="p-4 border" style={{ borderColor: C.border }} data-testid="hr-tracker-growth-chart">
        <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: C.gold }}>
          Active headcount · growth over time
        </div>
        <div style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.series || []}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
              <XAxis dataKey="month" fontSize={10} tickLine={false} tickFormatter={fmtMonYY} interval={Math.floor((data.series || []).length / 12) || 0} />
              <YAxis fontSize={10} tickLine={false} />
              <Tooltip />
              <Line type="monotone" dataKey="active_end" name="Active" stroke={C.plum} strokeWidth={2} dot={{ r: 2 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Seasonality — hires + separations by calendar month */}
      <Card className="p-4 border" style={{ borderColor: C.border }} data-testid="hr-tracker-seasonality">
        <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: C.gold }}>
          Seasonality · same-month totals across the window · {roleLabel}
        </div>
        <div style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={seasonality}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EEE" />
              <XAxis dataKey="month_label" fontSize={11} tickLine={false} />
              <YAxis fontSize={10} tickLine={false} />
              <Tooltip />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="hires" name="Hires" fill={C.ok} radius={[4,4,0,0]} maxBarSize={22} />
              <Bar dataKey="separations" name="Separations" fill={C.rose} radius={[4,4,0,0]} maxBarSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}
